/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Stepper } from "vr-react-components";
import { Button } from "vr-react-components";
import { setCanModify, setTourName, setTours } from "../store/slice/privateSlice";
import { setTvrexCertificate } from "../multiplayer/SocketConnection";
import { HOME_ROOM } from "../rooms/RoomNameConstants";

const Tour = () => {
    const currentRoom = useSelector((state) => state.room.history[0].ID);
    const tours = useSelector((state) => state.private.tours);
    const tour = useSelector((state) => state.private.tourName);

    const [currentStep, setCurrentStep] = useState(0);

    const dispatch = useDispatch();

    useEffect(() => {
        let tourName = "TOUR DASHBOARD ".concat(currentRoom.toUpperCase())

        if (tour) dispatch(setCanModify(false));

        if (currentRoom !== HOME_ROOM && tours.includes(tourName))
            dispatch(setTourName(tourName))
        
    }, [tour, tours, currentRoom, dispatch])

    const onClose = () => {
        dispatch(
            setTourName(tour === "FIRST USER EXPERIENCE" ?
                "TOUR DASHBOARD HOMEROOM" : false)
        )

        if (tour) {
            setTvrexCertificate(tour)
            dispatch(setTours(tours.filter(t => t !== tour)))
        }

        dispatch(setCanModify(true))
        setCurrentStep(0)
    };

    const primary = Button.constants.primary
    const danger = Button.constants.danger
    const dark = Button.constants.dark

    const data = {
        "FIRST USER EXPERIENCE": [
            {
                banner: true,
                title: "Welcome to T-VREX",
                imageSrc: "#ClaraChat",
                width: 2,
                height: 1,
                position: "0 1.7 -1",
                overlay: { width: 4, height: 3, position: "0 1.7 -1.1" },
                buttons: [
                    {
                        text: "No, thanks",
                        onClickHandler: onClose,
                        type: dark,
                        width: 0.5,
                    },
                    {
                        text: "Yes, show me around",
                        onClickHandler: "next",
                        type: primary,
                        width: 0.8,
                    },
                ],
                textSizeDecimal: 30,
                text:
                    "Hello, I am Clara, your  intelligent virtual assistant. Welcome to a new experience" +
                    "in Workload Automation!\n\nWould you like to discover our innovative hybrid VR/Desktop" +
                    " application through a quick guided tour?",
            },
            {
                width: 1,
                height: 0.5,
                position: "1.05 1.25 -1",
                overlay: { width: 4, height: 3, position: "0 1.7 -1.1" },
                buttons: [
                    {
                        text: "End Tour",
                        onClickHandler: onClose,
                        type: danger,
                        width: 0.3
                    },
                    {
                        text: "<",
                        onClickHandler: "back",
                        type: dark,
                        width: 0.2,
                    },
                    {
                        text: ">",
                        onClickHandler: "next",
                        type: primary,
                        width: 0.2,
                    },
                ],
                pointer: { side: "bottom", percentage: 1 },
                textSizeDecimal: 50,
                text:
                    "You are in the Desktop version.\n\nYou can switch to Desktop VR mode by clicking" +
                    " the VR button on the bottom right of your screen.\n\nYou can also enjoy this" +
                    " application in full VR mode if you have a cardboard support or a full VR headset.",
            },
            {
                width: 1.2,
                height: 0.6,
                position: "0 1.3 -1",
                overlay: { width: 4, height: 3, position: "0 1.7 -1.1" },
                buttons: [
                    {
                        text: "End Tour",
                        onClickHandler: onClose,
                        type: danger,
                        width: 0.3
                    },
                    {
                        text: "<",
                        onClickHandler: "back",
                        type: dark,
                        width: 0.2,
                    },
                    {
                        text: ">",
                        onClickHandler: "next",
                        width: 0.2,
                        type: primary,
                    },
                ],
                pointer: { side: "bottom", percentage: 0.5 },
                textSizeDecimal: 50,
                text:
                    "Use the below toolbar in the whole application.\n\nOn the left side, you can find" +
                    " the navigation buttons (Home, Back).\n\nOn the right side, you can find the" +
                    " Sharing button to  invite other users to collaborate in the troubleshooting session." +
                    "\n\nAlso, by clicking my avatar,  you can ask me for support, or select the Smart Actions" +
                    " I can run on your behalf.",
            },
            {
                banner: true,
                title: "Welcome to T-VREX",
                imageSrc: "#ClaraChat",
                width: 2,
                height: 1,
                position: "0 1.7 -1",
                overlay: { width: 5, height: 3, position: "0 1.7 -1.1" },
                buttons: [
                    {
                        text: "Start Troubleshooting",
                        onClickHandler: "next",
                        width: 1,
                        type: primary,
                    },
                ],
                textSizeDecimal: 30,
                text:
                    "You will start troubleshooting from the Home page that displays essential data in a " +
                    "complete dashboard.\n\nTo access any data, you have to select engines first.\n\nIf " +
                    "you need any help or want to start a guided tour, contact me by clicking the bubble speech icon.",
            },
            {
                width: 0.5,
                height: 0.3,
                position: "0.4 2.06 -1",
                overlay: { width: 3, height: 3, position: "-0.75 1.7 -1.1" },
                buttons: [],
                close: { onClickHandler: onClose },
                pointer: { side: "right", percentage: 0.5 },
                textSizeDecimal: 50,
                text:
                    "Select engines to visualize any data and start troubleshooting.",
            },
        ],
        "TOUR DASHBOARD HOMEROOM": [
            {
                width: 1,
                height: 0.7,
                position: "-0.1 1.8 -1",
                overlay: { width: 3, height: 3, position: "0.8 1.7 -1.1" },
                buttons: [
                    {
                        text: "End Tour",
                        onClickHandler: onClose,
                        type: danger,
                        width: 0.3
                    },
                    {
                        text: "<",
                        onClickHandler: "back",
                        type: dark,
                        width: 0.2,
                    },
                    {
                        text: ">",
                        onClickHandler: "next",
                        width: 0.2,
                        type: primary,
                    },
                ],
                pointer: { side: "left", percentage: 0.5 },
                text:
                    "The KPI monitoring panel gathers all the KPI cards,showing the status and details" +
                    " of the monitored objcts.\n\nYou can change the alert setting by clicking the related" +
                    " icon. For each KPI, you can access the list of critical objects by clicking on the KPI card.",
            },
            {
                width: 0.9,
                height: 0.7,
                position: "1.13 1.95 -1",
                overlay: [
                    { width: 2, height: 3, position: "1.7 1.7 -1.1" },
                    { width: 2, height: 3, position: "-1.7 1.7 -1.1" },
                ],
                buttons: [
                    {
                        text: "End Tour",
                        onClickHandler: onClose,
                        type: danger,
                        width: 0.22
                    },
                    {
                        text: "<",
                        onClickHandler: "back",
                        type: dark,
                        width: 0.2,
                    },
                    {
                        text: ">",
                        onClickHandler: "next",
                        type: primary,
                        width: 0.2,
                    },
                ],
                pointer: { side: "left", percentage: 0.8 },
                text:
                    "The central space is dedicated to the 3D graph visualization and manipulation.\n\nThe 3d " +
                    "chart is displayed below, you can  visualize some selected data and have some basic " +
                    "interaction (hover for tooltip access, zoom in, zoom out and 360° rotation).\n\nTitle and " +
                    "axis information are in the section above the 3D graph, beside the Explore button. The Eplore " +
                    "button opens the Graph 'Explore Room' where further features for data manipulation are available.",
            },
            {
                width: 1,
                height: 0.7,
                position: "0.1 1.8 -1",
                overlay: { width: 3, height: 3, position: "-0.75 1.7 -1.1" },
                buttons: [
                    {
                        text: "<",
                        onClickHandler: "back",
                        type: dark,
                        width: 0.2,
                    },
                    {
                        text: "End Tour",
                        onClickHandler: onClose,
                        type: danger,
                        width: 0.2,
                    },
                ],
                pointer: { side: "right", percentage: 0.5 },
                text:
                    "This panel shows different informative sections.\nThe first section shows information" +
                    " about the displayed data: the time range (current plan) and the engines selected. You " +
                    "can see and manage the selction by clicking the Engine Selection button.\n\nAlso, a " +
                    "summary of critical jobs is shown, with the related job status.\n\nBy clicking each " +
                    "vertical bar, you can see additional job status information.",
            },
        ],
        "TOUR DASHBOARD JOBROOM": [
            {
                width: 1,
                height: 0.7,
                position: "-0.1 1.8 -1",
                overlay: { width: 3, height: 3, position: "0.8 1.7 -1.1" },
                buttons: [
                    {
                        text: "End Tour",
                        onClickHandler: onClose,
                        type: danger,
                        width: 0.3
                    },
                    {
                        text: "<",
                        onClickHandler: "back",
                        type: dark,
                        width: 0.2,
                        disabled: true,
                    },
                    {
                        text: ">",
                        onClickHandler: "next",
                        type: primary,
                        width: 0.2,
                    },
                ],
                pointer: { side: "left", percentage: 0.5 },
                text:
                    "The left panel shows the essential JOB INFORMATION and a button to OPEN THE JOB LOG." +
                    "\n\nTHE CHART BELOW provides the minimum duration of the job during the LAST 10 RUNS. " +
                    "Data can be displayed aS A CHART OR AS TABLE, depending on your needs",
            },
            {
                width: 0.9,
                height: 0.9,
                position: "1.15 1.9 -1",
                overlay: [
                    { width: 2, height: 3, position: "1.7 1.7 -1.1" },
                    { width: 2, height: 3, position: "-1.7 1.7 -1.1" },
                ],
                buttons: [
                    {
                        text: "End Tour",
                        onClickHandler: onClose,
                        type: danger,
                        width: 0.14
                    },
                    {
                        text: "<",
                        onClickHandler: "back",
                        type: dark,
                        width: 0.2,
                    },
                    {
                        text: ">",
                        onClickHandler: "next",
                        type: primary,
                        width: 0.2,
                    },
                ],
                pointer: { side: "left", percentage: 0.8 },
                text:
                    "The central space is dedicated to 3D DATA VISUALIZATION." +
                    "\n\nIn the 3D GRAPH, you can view the workstation workload" +
                    "in the last 7 days and have some INTERACTIONS (hover over to" +
                    "see tooltip, zoom in, zoom out and 360° rotation).\n\nAbove" +
                    "the 3D GRAPH you can find the data description and axis" +
                    "information.\n\n Three buttons are available to DEEP DIVE into" +
                    "the EXPLORE ROOM, the DELAY ANALYSIS ROOM and the JOB STREAM" +
                    "VIEW respectively, where additional workload information is available.",
            },
            {
                width: 1,
                height: 0.7,
                position: "0.1 1.8 -1",
                overlay: { width: 3, height: 3, position: "-0.75 1.7 -1.1" },
                buttons: [
                    {
                        text: "<",
                        onClickHandler: "back",
                        type: dark,
                        width: 0.2,
                    },
                    {
                        text: "End Tour",
                        onClickHandler: onClose,
                        type: danger,
                        width: 0.2,
                    },
                ],
                pointer: { side: "right", percentage: 0.5 },
                text:
                    "The first part of the RIGHT PANEL contains the WORKSTATION properties." +
                    "\n\nThe chart below displays JOB STATISTICS by starting time or job duration.",
            },
        ],
        "TOUR DASHBOARD WORKSTATIONROOM": [
            {
                width: 1,
                height: 0.7,
                position: "-0.1 1.8 -1",
                overlay: { width: 3, height: 3, position: "0.8 1.7 -1.1" },
                buttons: [
                    {
                        text: "End Tour",
                        onClickHandler: onClose,
                        type: danger,
                        width: 0.3
                    },
                    {
                        text: "<",
                        onClickHandler: "back",
                        type: dark,
                        width: 0.2,
                    },
                    {
                        text: ">",
                        onClickHandler: "next",
                        width: 0.2,
                        type: primary,
                    },
                ],
                pointer: { side: "left", percentage: 0.5 },
                text:
                    "The upper section of the LEFT PANEL shows the WORKSTATION AND INFORMATION. PROPERTIES." +
                    "\n\nThe chart below displays the JOBS BY STATUS scheduled on the workstation. " +
                    "By clicking each vertical bar, you can see ADDITIONAL JOB STATUS INFORMATION",
            },
            {
                width: 0.9,
                height: 0.9,
                position: "1.15 1.9 -1",
                overlay: [
                    { width: 2, height: 3, position: "1.7 1.7 -1.1" },
                    { width: 2, height: 3, position: "-1.7 1.7 -1.1" },
                ],
                buttons: [
                    {
                        text: "End Tour",
                        onClickHandler: onClose,
                        type: danger,
                        width: 0.14
                    },
                    {
                        text: "<",
                        onClickHandler: "back",
                        type: dark,
                        width: 0.2,
                    },
                    {
                        text: ">",
                        onClickHandler: "next",
                        width: 0.2,
                        type: primary,
                    },
                ],
                pointer: { side: "left", percentage: 0.8 },
                text:
                    "The central space is dedicated to 3D DATA VISUALIZATION.\n\nIn the 3D GRAPH, " +
                    "you can view the workstation workload in the last 7 days and have some INTERACTIONS " +
                    "(hover over to see tooltip, zoom in, zoom out and 360° rotation).\n\nAbove the 3D " +
                    "GRAPH you can find the data description and axis information.\n\nBy clicking the " +
                    "EXPLORE button you can enter in the EXPLORE ROOM where additional workload information" +
                    " is available.",
            },
            {
                width: 1,
                height: 0.6,
                position: "0.1 1.8 -1",
                overlay: { width: 3, height: 3, position: "-0.75 1.7 -1.1" },
                buttons: [
                    {
                        text: "<",
                        onClickHandler: "back",
                        width: 0.2,
                        type: dark
                    },
                    {
                        text: "End Tour",
                        onClickHandler: onClose,
                        width: 0.2,
                        type: danger,
                    },
                ],
                pointer: { side: "right", percentage: 0.5 },
                text:
                    "At the top of the right panel of the workstation room you can access only the number " +
                    "of CRITICAL JOBS by risk level.\n\n At the bottom you can access the detailed list of" +
                    " CRITICAL JOBS and the relative JOB ROOMS.\n\n"
            },
        ],
    }

    return tour && tours.includes(tour) ? <Stepper
        steps={data}
        field={tour}
        aframeClasses={"collidable"}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
    /> : null;
};

export default Tour;
